import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Instagram, Facebook } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { CONTACT, BUILDINGS } from "../data/apartments";
import { Seo } from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t, lang } = useLang();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", building: "", apartment: "", message: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const b = params.get("building");
    const a = params.get("apartment");
    if (b || a) setForm((f) => ({ ...f, building: b || "", apartment: a || "" }));
  }, [params]);

  const aptOptions = form.building && BUILDINGS[form.building] ? BUILDINGS[form.building].apartments : [];

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message,
        building: form.building || null,
        apartment: form.apartment || null,
        source: "contact_form",
        language: lang,
      });
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", phone: "", building: "", apartment: "", message: "" });
    } catch (err) {
      toast.error(t("contact.error"));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border border-cream/20 focus:border-gold text-cream px-4 py-3 outline-none transition-colors placeholder:text-cream/30 font-sans text-sm";
  const labelCls = "block text-[11px] uppercase tracking-widest text-gold/70 mb-2";

  return (
    <div data-testid="contact-page" className="pt-28">
      <Seo
        title="Kontaktai — Sakurų namai Marijampolėje"
        description="Susisiekite dėl naujų A++ butų Sakurų namai, Parko g. 1D, Marijampolė. Tel. +370 636 10123, el. paštas sakurunamai@gmail.com."
        path="/kontaktai"
      />
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">{t("contact.subtitle")}</p>
        <h1 className="font-serif text-5xl sm:text-6xl font-light text-cream">{t("contact.title")}</h1>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-24 grid grid-cols-1 lg:grid-cols-2 gap-16">
        {/* Form */}
        <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>{t("contact.name")}</label>
              <input required data-testid="contact-name" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>{t("contact.phone")}</label>
              <input required data-testid="contact-phone" className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
          </div>
          <div>
            <label className={labelCls}>{t("contact.email")}</label>
            <input required type="email" data-testid="contact-email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>{t("contact.building")}</label>
              <select
                data-testid="contact-building"
                className={`${inputCls} appearance-none`}
                value={form.building}
                onChange={(e) => setForm({ ...form, building: e.target.value, apartment: "" })}
              >
                <option value="" className="bg-charcoal">{t("contact.selectBuilding")}</option>
                <option value="Migdolas" className="bg-charcoal">Migdolas</option>
                <option value="Vyšnia" className="bg-charcoal">Vyšnia</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>{t("contact.apartmentSelect")}</label>
              <select
                data-testid="contact-apartment"
                className={`${inputCls} appearance-none`}
                value={form.apartment}
                onChange={(e) => setForm({ ...form, apartment: e.target.value })}
                disabled={!form.building}
              >
                <option value="" className="bg-charcoal">{t("contact.none")}</option>
                {aptOptions.map((a) => (
                  <option key={a.code} value={a.code} className="bg-charcoal">{a.unit} · {a.area} m² · {a.rooms} k.</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>{t("contact.message")}</label>
            <textarea data-testid="contact-message" rows={5} className={inputCls} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit"
            className="inline-flex items-center gap-2 border border-gold/50 text-gold text-xs uppercase tracking-widest px-8 py-4 hover:bg-gold/10 transition-colors disabled:opacity-50"
          >
            <Send size={14} /> {loading ? t("contact.sending") : t("contact.send")}
          </button>
        </form>

        {/* Contact details */}
        <div className="space-y-8">
          <div className="space-y-5">
            <a href={`tel:${CONTACT.phone}`} data-testid="contact-detail-phone" className="flex items-center gap-4 group">
              <span className="w-11 h-11 rounded-full border border-gold/30 flex items-center justify-center group-hover:bg-gold/10 transition-colors">
                <Phone size={18} className="text-gold" />
              </span>
              <div>
                <p className="text-[11px] uppercase tracking-widest text-cream/40">{t("contact.callUs")}</p>
                <p className="text-cream text-lg">{CONTACT.phoneDisplay}</p>
              </div>
            </a>
            <a href={`mailto:${CONTACT.email}`} data-testid="contact-detail-email" className="flex items-center gap-4 group">
              <span className="w-11 h-11 rounded-full border border-gold/30 flex items-center justify-center group-hover:bg-gold/10 transition-colors">
                <Mail size={18} className="text-gold" />
              </span>
              <div>
                <p className="text-[11px] uppercase tracking-widest text-cream/40">{t("contact.emailUs")}</p>
                <p className="text-cream text-lg">{CONTACT.email}</p>
              </div>
            </a>
            <div className="flex items-center gap-4">
              <span className="w-11 h-11 rounded-full border border-gold/30 flex items-center justify-center">
                <MapPin size={18} className="text-gold" />
              </span>
              <div>
                <p className="text-[11px] uppercase tracking-widest text-cream/40">{t("contact.addressLabel")}</p>
                <p className="text-cream">{CONTACT.address}</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4 pt-4">
            <a
              href={`https://wa.me/${CONTACT.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp"
              className="inline-flex items-center justify-center gap-2 text-xs uppercase tracking-widest px-6 py-4 text-cream transition-colors"
              style={{ backgroundColor: "#25D366" }}
            >
              {t("contact.whatsappCta")}
            </a>
            <div className="flex items-center gap-3">
              <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram" aria-label="Instagram"
                className="w-12 h-12 rounded-full border border-gold/30 flex items-center justify-center text-gold hover:bg-gold/10 transition-colors">
                <Instagram size={20} />
              </a>
              <a href={CONTACT.facebook} target="_blank" rel="noopener noreferrer" data-testid="contact-facebook" aria-label="Facebook"
                className="w-12 h-12 rounded-full border border-gold/30 flex items-center justify-center text-gold hover:bg-gold/10 transition-colors">
                <Facebook size={20} />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
